/**
 * Admin product type config Redux slice — CON-MAT2-005.
 *
 * Manages create/edit form state for ProductTypeConfig entities: draft values,
 * save status, API error messages, and field-level 422 errors that map to
 * React Hook Form `setError` calls.
 *
 * Registered in `main.tsx` via `extendStoreReducer('adminProductTypeConfig', ...)`.
 *
 * Per `.claude/rules/frontend-data-flow.md` §3:
 *   - Thunks own all axios logic; containers dispatch thunks.
 *   - Side effects (toasts, navigation) are handled by the container, NOT here.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { isApiError } from '@dfq/shared/api/_envelope';
import {
  getProductTypeConfig,
  createProductTypeConfig as createProductTypeConfigApi,
  updateProductTypeConfig as updateProductTypeConfigApi,
} from '@dfq/shared/api/admin/productTypeConfigs';
import type {
  ProductTypeConfigDTO,
  CreateProductTypeConfigPayload,
  UpdateProductTypeConfigPayload,
} from '@dfq/shared/types/productTypes';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export interface AdminProductTypeConfigState {
  /** Currently-loaded entity (populated on edit load; null when in create mode). */
  draft: ProductTypeConfigDTO | null;
  /** Last successfully persisted entity. */
  saved: ProductTypeConfigDTO | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  /** Field-level 422 validation errors keyed by field name. */
  fieldErrors: Record<string, string[]>;
}

const initialState: AdminProductTypeConfigState = {
  draft: null,
  saved: null,
  status: 'idle',
  error: null,
  fieldErrors: {},
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchProductTypeConfig = createAsyncThunk(
  'adminProductTypeConfig/fetch',
  async (id: number, { rejectWithValue }) => {
    try {
      return await getProductTypeConfig(id);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ message: err.message, fieldErrors: err.errors ?? {} });
      }
      return rejectWithValue({ message: 'Failed to load product type config', fieldErrors: {} });
    }
  },
);

export const createProductTypeConfig = createAsyncThunk(
  'adminProductTypeConfig/create',
  async (payload: CreateProductTypeConfigPayload, { rejectWithValue }) => {
    try {
      return await createProductTypeConfigApi(payload);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ message: err.message, fieldErrors: err.errors ?? {} });
      }
      return rejectWithValue({ message: 'Failed to create product type config', fieldErrors: {} });
    }
  },
);

export const updateProductTypeConfig = createAsyncThunk(
  'adminProductTypeConfig/update',
  async (
    { id, payload }: { id: number; payload: UpdateProductTypeConfigPayload },
    { rejectWithValue },
  ) => {
    try {
      return await updateProductTypeConfigApi(id, payload);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ message: err.message, fieldErrors: err.errors ?? {} });
      }
      return rejectWithValue({ message: 'Failed to update product type config', fieldErrors: {} });
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const adminProductTypeConfigSlice = createSlice({
  name: 'adminProductTypeConfig',
  initialState,
  reducers: {
    resetSlice: () => initialState,
    clearFieldErrors(state) {
      state.fieldErrors = {};
    },
  },
  extraReducers: (builder) => {
    // Fetch
    builder
      .addCase(fetchProductTypeConfig.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(fetchProductTypeConfig.fulfilled, (state, action: PayloadAction<ProductTypeConfigDTO>) => {
        state.status = 'succeeded';
        state.draft = action.payload;
        state.saved = action.payload;
      })
      .addCase(fetchProductTypeConfig.rejected, (state, action) => {
        state.status = 'failed';
        const payload = action.payload as { message: string; fieldErrors: Record<string, string[]> };
        state.error = payload?.message ?? 'Failed to load';
        state.fieldErrors = payload?.fieldErrors ?? {};
      });

    // Create
    builder
      .addCase(createProductTypeConfig.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(createProductTypeConfig.fulfilled, (state, action: PayloadAction<ProductTypeConfigDTO>) => {
        state.status = 'succeeded';
        state.draft = action.payload;
        state.saved = action.payload;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(createProductTypeConfig.rejected, (state, action) => {
        state.status = 'failed';
        const payload = action.payload as { message: string; fieldErrors: Record<string, string[]> };
        state.error = payload?.message ?? 'Failed to create';
        state.fieldErrors = payload?.fieldErrors ?? {};
      });

    // Update
    builder
      .addCase(updateProductTypeConfig.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(updateProductTypeConfig.fulfilled, (state, action: PayloadAction<ProductTypeConfigDTO>) => {
        state.status = 'succeeded';
        state.draft = action.payload;
        state.saved = action.payload;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(updateProductTypeConfig.rejected, (state, action) => {
        state.status = 'failed';
        const payload = action.payload as { message: string; fieldErrors: Record<string, string[]> };
        state.error = payload?.message ?? 'Failed to update';
        state.fieldErrors = payload?.fieldErrors ?? {};
      });
  },
});

export const { resetSlice, clearFieldErrors } = adminProductTypeConfigSlice.actions;
export const adminProductTypeConfigReducer = adminProductTypeConfigSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface AdminProductTypeConfigRootStateSlice {
  adminProductTypeConfig: AdminProductTypeConfigState;
}

export const selectProductTypeConfigDraft = (
  state: AdminProductTypeConfigRootStateSlice,
): ProductTypeConfigDTO | null => state.adminProductTypeConfig.draft;

export const selectProductTypeConfigSaved = (
  state: AdminProductTypeConfigRootStateSlice,
): ProductTypeConfigDTO | null => state.adminProductTypeConfig.saved;

export const selectProductTypeConfigStatus = (
  state: AdminProductTypeConfigRootStateSlice,
): AdminProductTypeConfigState['status'] => state.adminProductTypeConfig.status;

export const selectProductTypeConfigError = (
  state: AdminProductTypeConfigRootStateSlice,
): string | null => state.adminProductTypeConfig.error;

export const selectProductTypeConfigFieldErrors = (
  state: AdminProductTypeConfigRootStateSlice,
): Record<string, string[]> => state.adminProductTypeConfig.fieldErrors;
